import { faArrowRight, faGlobe, faRotateRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Domain } from '@feathr/blackbox';
import { EPinpointRequestStatus } from '@feathr/blackbox';
import { Button, CardV2, Icon, Text, Tooltip } from '@feathr/components';
import { DOMAIN_SETUP_ARTICLE_URL } from '@feathr/extender/App/Settings/Account/DomainsPage/constants';
import {
  ContentStatusPill,
  DomainRecordStatusPill,
  EmailStatusPill,
} from '@feathr/extender/components/DomainStatusPill';
import { useRefreshDomain } from '@feathr/extender/hooks';
import { useFlags } from '@feathr/extender/state';

import AuthenticateDomainButton from './AuthenticateDomainButton';
import DomainField from './DomainField';

import * as styles from './DomainOverviewCard.css';

interface IProps {
  domain: Domain;
}

function DomainOverviewCard({ domain }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const flags = useFlags();
  const refreshDomain = useRefreshDomain(domain);

  const isEmailReady = domain.emailSendingStatus(flags.bypassMX) === EPinpointRequestStatus.Success;
  const isComplete = domain.isContentServingReady && isEmailReady;
  const canRefresh = domain.needsEmailAuthentication(flags.bypassMX);

  function handleClickRefresh(): void {
    void refreshDomain();
  }

  return (
    <CardV2 width={'full'}>
      <CardV2.Header
        title={domain.displayDomain}
        titlePrefix={
          <span className={styles.globe}>
            <Icon icon={faGlobe} size={5} />
          </span>
        }
      >
        <div className={styles.actions}>
          {canRefresh && (
            <Tooltip
              title={t(
                'Restart the email domain verification process. This may take up to 48 hours to complete.',
              )}
            >
              <Button
                onClick={handleClickRefresh}
                prefix={<FontAwesomeIcon icon={faRotateRight} />}
              >
                {t('Refresh status')}
              </Button>
            </Tooltip>
          )}
          <AuthenticateDomainButton domain={domain} />
        </div>
      </CardV2.Header>
      <CardV2.Content>
        <div className={styles.statuses}>
          <DomainField
            label={t('Content serving status')}
            tooltip={t(
              "This domain's status for serving landing pages, partner dashboards, and invite pages.",
            )}
          >
            <ContentStatusPill domain={domain} />
          </DomainField>
          <DomainField
            label={t('Email sending status')}
            tooltip={t("This domain's status for sending emails from Feathr.")}
          >
            <EmailStatusPill domain={domain} />
          </DomainField>
          <DomainField
            label={t('DKIM status')}
            tooltip={t(
              'DomainKeys Identified Mail (DKIM) verifies that emails are sent from an authorized server.',
            )}
          >
            <DomainRecordStatusPill
              isLoading={domain.isPending}
              status={domain.get('dkim')?.status ?? null}
            />
          </DomainField>
          <DomainField
            label={t('MX status')}
            tooltip={t('Mail Exchange (MX) records direct incoming email to your mail server.')}
          >
            <DomainRecordStatusPill
              isLoading={domain.isPending}
              status={domain.get('mx')?.status ?? null}
            />
          </DomainField>
        </div>
        {!isComplete && (
          <div className={styles.propagation}>
            <Text variant={'defaultRegular'}>
              {t('Changes may take up to 48 hours to go live.')}
            </Text>
            <Button
              className={styles.learnMore}
              href={DOMAIN_SETUP_ARTICLE_URL}
              suffix={<FontAwesomeIcon icon={faArrowRight} />}
              target={'_blank'}
              type={'link'}
            >
              {t('Learn more about domain setup')}
            </Button>
          </div>
        )}
      </CardV2.Content>
    </CardV2>
  );
}

export default observer(DomainOverviewCard);
